/**
 * 화면이 쓰는 권한 묶음 (PRD2 T-1.13, §11.2·§11.3).
 * `can()`의 행동을 간트·시트·인스펙터·툴바가 같은 기준으로 읽게 한다.
 */

import { can, type MemberPlanRole, type PlanActor, type PlanLifecycle } from "./can";

export type PlanSurface = {
  role: MemberPlanRole;
  editWbs: boolean;
  editSchedule: boolean;
  editProgress: boolean;
  assignResource: boolean;
  editTask: boolean;
  editIssue: boolean;
  createBaseline: boolean;
  approveBaseline: boolean;
  exportPlan: boolean;
  /** 일정·진척을 고칠 수 있으면 실행 취소도 남긴다. */
  undo: boolean;
  /** 열람자. 상단 배너와 저장 표시를 이 값으로 가른다. */
  readOnly: boolean;
};

export function planSurface(actor: PlanActor, status: PlanLifecycle = "active"): PlanSurface {
  const resource = { status };
  const editWbs = can(actor, "plan.wbs.edit", resource);
  const editSchedule = can(actor, "plan.schedule.edit", resource);
  const editProgress = can(actor, "plan.progress.editOwn", resource);
  const assignResource = can(actor, "plan.resource.assign", resource);
  const editTask = can(actor, "plan.task.edit", resource);
  const editIssue = can(actor, "plan.issue.edit", resource);
  return {
    role: actor.role,
    editWbs,
    editSchedule,
    editProgress,
    assignResource,
    editTask,
    editIssue,
    createBaseline: can(actor, "plan.baseline.create", resource),
    approveBaseline: can(actor, "plan.baseline.approve", resource),
    exportPlan: can(actor, "plan.export", resource),
    undo: editWbs || editSchedule || editProgress,
    readOnly: status === "archived" || actor.role === "viewer",
  };
}

const WBS_COLUMNS = new Set(["name", "preds", "note"]);
const SCHEDULE_COLUMNS = new Set(["mm", "duration", "start", "finish"]);
const ASSIGN_COLUMNS = new Set(["assignee"]);
const PROGRESS_COLUMNS = new Set(["percent"]);

/** 시트 열이 이 역할에서 편집 모드로 들어가는지. 요약 행 여부는 그리드가 따로 본다. 기여자 진척은 본인 행만. */
export function columnWritable(col: string, surface: PlanSurface, own = true): boolean {
  if (WBS_COLUMNS.has(col)) return surface.editWbs;
  if (SCHEDULE_COLUMNS.has(col)) return surface.editSchedule;
  if (ASSIGN_COLUMNS.has(col)) return surface.assignResource;
  if (PROGRESS_COLUMNS.has(col)) return surface.editProgress && (surface.role !== "contributor" || own);
  return false;
}
